<template>
  <div id="app">
    <nav class="github-navbar" v-if="!roteMeta.noHeader">
      <div class="navbar-left">
        <button class="menu-btn mobile-menu" @click="drawerOpen = true">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            style="vertical-align: middle; margin-right: 8px"
          >
            <path
              fill="currentColor"
              d="M3 6h18v2H3V6m0 5h18v2H3v-2m0 5h18v2H3v-2z"
            />
          </svg>
        </button>
        <img
          src="@/assets/logo.png"
          alt="Logo"
          class="navbar-logo"
          draggable="false"
          @click="$router.push('/')"
        />
        <span class="navbar-title">{{ pageTitle }}</span>
        <ul class="navbar-menu desktop-menu">
          <li v-for="item in menuItems" :key="item.path">
            <router-link :to="item.path">{{ item.name }}</router-link>
          </li>
        </ul>
      </div>
      <transition name="drawer">
        <div
          v-if="drawerOpen"
          class="drawer-overlay mobile-menu"
          @click.self="drawerOpen = false"
        >
          <div class="drawer-menu" @click.stop>
            <div class="drawer-header">
              <span>菜单</span>
              <button class="drawer-close" @click="drawerOpen = false">
                ✕
              </button>
            </div>
            <ul class="drawer-menu-ul">
              <li v-for="item in menuItems" :key="item.path">
                <router-link :to="item.path" @click="drawerOpen = false">{{
                  item.name
                }}</router-link>
              </li>
            </ul>
          </div>
        </div>
      </transition>
    </nav>
    <router-view v-slot="{ Component, route }">
      <transition name="fade-slide" mode="out-in">
        <component :is="Component" :key="route.fullPath" />
      </transition>
    </router-view>
  </div>
</template>


<script lang="ts" setup>
import { ref, onMounted, computed, watch } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();

const roteMeta = ref(route.meta);

//监听路由变化，获取路由mate
watch(
  () => route.meta,
  (newMeta) => (roteMeta.value = newMeta),
  { immediate: true }
);

const menuItems = [
  {
    name: "隐私政策摘要",
    path: "/privacy_policy_brief",
    title: "隐私政策摘要",
  },
  { name: "用户协议", path: "/user_agreement", title: "用户协议" },
  { name: "隐私政策", path: "/privacy_policy", title: "隐私政策" },
  { name: "权限说明", path: "/permission_description", title: "权限说明" },
  { name: "SDK说明", path: "/SDK", title: "SDK说明" },
  {
    name: "个人信息收集清单",
    path: "/personal_information",
    title: "个人信息收集清单",
  },
  { name: "常见问题解答", path: "/faq", title: "常见问题解答" },
];

const pageTitle = computed(() => {
  const found = menuItems.find((item) => item.path === route.path);
  return found ? found.title : "政律通";
});

const theme = ref("light");
const drawerOpen = ref(false);

const setTheme = (t: string) => {
  theme.value = t;
  if (t === "dark") {
    document.documentElement.style.setProperty("--background-color", "#181a1b");
    document.documentElement.style.setProperty("--text-color", "#c9d1d9");
    document.documentElement.style.setProperty("--navbar-bg", "#161b22");
    document.documentElement.style.setProperty("--navbar-border", "#21262d");
    document.documentElement.style.setProperty("--table-border-color", "#333");
    document.documentElement.style.setProperty("--table-head-color", "#1e1e1e");
    // FAQ 暗色变量
    document.documentElement.style.setProperty("--faq-title-color", "#e6edf3");
    document.documentElement.style.setProperty("--faq-input-bg", "#23272f");
    document.documentElement.style.setProperty("--faq-input-color", "#e6edf3");
    document.documentElement.style.setProperty("--faq-list-bg", "#23272f");
    document.documentElement.style.setProperty(
      "--faq-group-toggle-bg",
      "#23272f"
    );
    document.documentElement.style.setProperty("--faq-li-bg", "#23272f");
    document.documentElement.style.setProperty("--no-results-bg", "#23272f");
    document.documentElement.style.setProperty(
      "--no-results-title-color",
      "#58a6ff"
    );
  } else {
    document.documentElement.style.setProperty("--background-color", "#ffffff");
    document.documentElement.style.setProperty("--text-color", "#24292f");
    document.documentElement.style.setProperty("--navbar-bg", "#f6f8fa");
    document.documentElement.style.setProperty("--navbar-border", "#d8dee4");
    document.documentElement.style.setProperty("--table-border-color", "#ccc");
    document.documentElement.style.setProperty("--table-head-color", "#d0e8f3");
    // FAQ 亮色变量
    document.documentElement.style.setProperty("--faq-title-color", "#24292f");
    document.documentElement.style.setProperty("--faq-input-bg", "#fff");
    document.documentElement.style.setProperty("--faq-input-color", "#24292f");
    document.documentElement.style.setProperty("--faq-list-bg", "#f6f8fa");
    document.documentElement.style.setProperty(
      "--faq-group-toggle-bg",
      "#f6f8fa"
    );
    document.documentElement.style.setProperty("--faq-li-bg", "#fff");
    document.documentElement.style.setProperty("--no-results-bg", "#fff");
    document.documentElement.style.setProperty(
      "--no-results-title-color",
      "#2188ff"
    );
  }
};

onMounted(() => {
  setTheme("light");
  window.setTheme = setTheme;
});
</script>


<style lang="scss">
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  background: var(--background-color);
  color: var(--text-color);
}

.github-navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2rem;
  height: 56px;
  background: var(--navbar-bg);
  border-bottom: 1px solid var(--navbar-border);
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(27, 31, 35, 0.04);
}
.navbar-left {
  display: flex;
  align-items: center;
}
.navbar-logo {
  width: 32px;
  height: 32px;
  margin-right: 12px;
  border-radius: 6px;
  user-select: none;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}
.navbar-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-color);
  letter-spacing: 1px;
  margin-right: 24px;
  user-select: none;
  cursor: pointer;
}
.navbar-menu {
  display: flex;
  list-style: none;
  margin: 0 0 0 8px;
  padding: 0;
  background: none;
}
.navbar-menu li {
  margin: 0 4px;
}
.navbar-menu a {
  color: var(--text-color);
  text-decoration: none;
  font-size: 16px;
  padding: 6px 14px;
  border-radius: 6px;
  transition: background 0.2s, color 0.2s;
  font-weight: 500;
}
.navbar-menu a.router-link-active {
  background: #2188ff22;
  color: #2188ff;
}
.navbar-menu a:hover {
  background: #2188ff11;
  color: #2188ff;
}
.menu-btn {
  display: none;
  background: none;
  border: none;
  margin-left: 0;
  margin-right: 0;
  cursor: pointer;
  color: var(--text-color);
  border-radius: 6px;
  padding: 6px;
  transition: background 0.2s;
  align-items: center;
  justify-content: center;
}
.menu-btn svg {
  display: block;
}
.menu-btn:hover {
  background: var(--navbar-border);
}
.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.18);
  z-index: 9998;
  display: flex;
  align-items: flex-start;
}
.drawer-menu {
  position: relative;
  top: 0;
  left: 0;
  width: 80vw;
  max-width: 320px;
  height: 100vh;
  background: var(--navbar-bg);
  box-shadow: 2px 0 16px rgba(27, 31, 35, 0.12);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  animation: drawer-in 0.3s;
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
}
.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 16px 12px 16px;
  border-bottom: 1px solid var(--navbar-border);
  font-size: 19px;
  font-weight: 700;
}
.drawer-close {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: var(--text-color);
  border-radius: 6px;
  padding: 4px 8px;
  transition: background 0.2s;
}
.drawer-close:hover {
  background: #2188ff11;
  color: #2188ff;
}
.drawer-menu ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.drawer-menu li {
  margin: 0;
}
.drawer-menu a {
  display: block;
  color: var(--text-color);
  text-decoration: none;
  font-size: 18px;
  padding: 16px 24px;
  border-bottom: 1px solid var(--navbar-border);
  transition: background 0.2s, color 0.2s;
  font-weight: 500;
}
.drawer-menu a.router-link-active,
.drawer-menu a:hover {
  background: #2188ff11;
  color: #2188ff;
}
@keyframes drawer-in {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}
.mobile-menu {
  display: none;
}
.desktop-menu {
  display: flex;
}
@media (max-width: 1080px) {
  .github-navbar {
    padding: 0 0.5rem;
  }
  .navbar-left {
    width: 100%;
    justify-content: flex-start;
  }
  .desktop-menu {
    display: none;
  }
  .mobile-menu {
    display: block;
  }
  .navbar-title {
    font-size: 18px;
    margin-right: 8px;
  }
  .menu-btn {
    margin-left: 0;
    margin-right: 8px;
  }
}
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(16px);
}
.fade-slide-leave-from,
.fade-slide-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.drawer-menu-ul {
  flex: 1;
  overflow-y: auto;
}
</style>
